/**
 * Form schema configuration resolver.
 *
 * Maps a submission_type to the form schema (sections) plus the human-facing
 * strings each lifecycle page needs (titles, submit-button labels, doc titles).
 * This is the single place that knows "which schema does this submission use".
 *
 * Behavior contract for NEW_SUBMISSION: `sections` is the IDENTICAL array
 * reference (initialSubmissionFormSections === FormSubmissionFields.formSections),
 * so existing initial-submission behavior (progress %, PDF/MD byte output) is
 * unchanged.
 *
 * pdfTitle / markdownTitle are recorded here for completeness, but note the
 * generators in docGenerationService currently HARD-CODE their titles and do not
 * accept a title parameter, so these strings are not yet threaded through. See
 * the CR pages — they call the same generators, which still emit the initial
 * titles. Changing that would require editing docGenerationService (not owned by
 * this work).
 */

import { FormSection } from '../components/FormSubmissionFields';
import { SubmissionType } from '../lib/supabaseClient';
import { initialSubmissionFormSections } from './initialSubmissionForm';
import { continuingReviewFormSections } from './continuingReviewForm';

export interface FormSchemaConfig {
  submissionType: SubmissionType;
  sections: FormSection[];
  title: string;
  draftSubmitLabel: string;
  needsActionSubmitLabel: string;
  readOnlyTitle: string;
  pdfTitle: string;
  markdownTitle: string;
}

const NEW_SUBMISSION_CONFIG: FormSchemaConfig = {
  submissionType: 'NEW_SUBMISSION',
  sections: initialSubmissionFormSections,
  title: 'Study Submission Form',
  draftSubmitLabel: 'Submit',
  needsActionSubmitLabel: 'Send Revised Submission For IRB Review',
  readOnlyTitle: 'Submission Form (Locked View)',
  // Current hard-coded strings in docGenerationService (kept identical).
  pdfTitle: 'Study Submission Form - PDF Version',
  markdownTitle: '# Timely IRB Submission - Markdown Export',
};

const CONTINUING_REVIEW_CONFIG: FormSchemaConfig = {
  submissionType: 'CONTINUING_REVIEW',
  sections: continuingReviewFormSections,
  title: 'Continuing Review',
  draftSubmitLabel: 'Submit',
  needsActionSubmitLabel: 'Send Revised Submission For IRB Review',
  readOnlyTitle: 'Continuing Review (Locked View)',
  // Aspirational CR titles — not yet threaded through the generators (see header).
  pdfTitle: 'Continuing Review Form - PDF Version',
  markdownTitle: '# Timely IRB Continuing Review - Markdown Export',
};

/**
 * Resolve the schema config for a submission type.
 * AMENDMENT throws — amendment forms are not built yet. Lifecycle *views* that
 * must not crash on amendments should fall back to the NEW_SUBMISSION config
 * rather than calling this with 'AMENDMENT'.
 */
export function getFormSchemaConfig(submissionType: SubmissionType): FormSchemaConfig {
  switch (submissionType) {
    case 'NEW_SUBMISSION':
      return NEW_SUBMISSION_CONFIG;
    case 'CONTINUING_REVIEW':
      return CONTINUING_REVIEW_CONFIG;
    case 'AMENDMENT':
      throw new Error('Amendment forms are not supported yet');
    default:
      throw new Error(`Unknown submission type: ${submissionType}`);
  }
}

/**
 * Non-throwing resolver for read-only / list views: CONTINUING_REVIEW resolves
 * to the CR schema; everything else (NEW_SUBMISSION, AMENDMENT, unknown) falls
 * back to the initial-submission schema. Use this where a stray amendment row
 * must render rather than crash.
 */
export function resolveFormSchemaConfig(submissionType: string | null | undefined): FormSchemaConfig {
  return submissionType === 'CONTINUING_REVIEW' ? CONTINUING_REVIEW_CONFIG : NEW_SUBMISSION_CONFIG;
}
